import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import {
    CounterpartyReference,
    MAX_COUNTERPARTY_REFERENCES,
} from "@common/schemas/counterpartyReference.schema";
import {
    DraftRefsState,
    DraftRefsTarget,
    useDraftRefsByTarget,
    useTargetDraftRefs,
} from "./useDraftRefsByTarget";

export interface CounterpartyRefContextValue extends DraftRefsState<CounterpartyReference> {
    focusedCounterpartyId: string | null;
    setFocusedCounterpartyId: React.Dispatch<
        React.SetStateAction<string | null>
    >;
}

function buildDraftRefDedupKey(ref: CounterpartyReference): string {
    return `${ref.taskId}::${ref.counterpartyId}`;
}

const CounterpartyRefContext =
    createContext<CounterpartyRefContextValue | null>(null);

interface CounterpartyRefProviderProps {
    taskId: string;
    children: React.ReactNode;
}

export const CounterpartyRefProvider: React.FC<
    CounterpartyRefProviderProps
> = ({ taskId, children }) => {
    const draftRefsState = useDraftRefsByTarget({
        taskId,
        maxRefs: MAX_COUNTERPARTY_REFERENCES,
        getDedupKey: buildDraftRefDedupKey,
    });
    const [focusedCounterpartyId, setFocusedCounterpartyId] = useState<
        string | null
    >(null);

    useEffect(() => {
        setFocusedCounterpartyId(null);
    }, [taskId]);

    const value = useMemo(
        () => ({
            ...draftRefsState,
            focusedCounterpartyId,
            setFocusedCounterpartyId,
        }),
        [draftRefsState, focusedCounterpartyId],
    );

    return (
        <CounterpartyRefContext.Provider value={value}>
            {children}
        </CounterpartyRefContext.Provider>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export function useCounterpartyRefContextMaybe(): CounterpartyRefContextValue | null {
    return useContext(CounterpartyRefContext);
}

// eslint-disable-next-line react-refresh/only-export-components
export function useDraftCounterpartyRefsForTarget(target: DraftRefsTarget) {
    return useTargetDraftRefs({
        draftRefsState: useCounterpartyRefContextMaybe(),
        target,
    });
}
